import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Badge,
  Flex,
} from '@chakra-ui/react';
import { getTerminationStats, getRecentSubmissions } from '../services/nihTerminationService';

const Stats = () => {
  const [stats, setStats] = useState(null);
  const [riskDistribution, setRiskDistribution] = useState([]);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch statistics
        const result = await getTerminationStats();
        if (result.success) {
          setStats(result.stats);
          setRiskDistribution(result.riskDistribution || []);
        } else {
          setError(result.error || 'Failed to fetch statistics');
        }
        
        // Fetch recent submissions
        const submissionsResult = await getRecentSubmissions(5);
        if (submissionsResult.success) {
          setRecentSubmissions(submissionsResult.submissions);
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  if (loading) {
    return (
      <Container maxW="800px" py={10} textAlign="center">
        <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
        <Text mt={4}>Loading statistics...</Text>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="800px" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertTitle mr={2}>Error!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </Container>
    );
  }

  // Helper to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };

  // Find the total number of submissions from all risk levels
  const totalRiskResponses = riskDistribution?.reduce((sum, item) => sum + item.count, 0) || 0;

  return (
    <Container maxW="800px" py={10}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading as="h1" size="xl" mb={4}>
            NIH Termination Statistics
          </Heading>
          <Text fontSize="lg">
            Overview of the NIH grant terminations reported through our platform
          </Text>
        </Box>
        
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Stat bg="white" p={5} borderRadius="md" boxShadow="md" textAlign="center">
            <StatLabel>Total Reports</StatLabel>
            <StatNumber>{stats?.total_terminations || 0}</StatNumber>
            <StatHelpText>Termination notices submitted</StatHelpText>
          </Stat>
                    
          <Stat bg="white" p={5} borderRadius="md" boxShadow="md" textAlign="center">
            <StatLabel>Avg Risk Assessment</StatLabel>
            <StatNumber>{stats?.avg_risk_assessment ? Number(stats.avg_risk_assessment).toFixed(1) : 'N/A'}</StatNumber>
            <StatHelpText>1 = Unlikely, 5 = Likely</StatHelpText>
          </Stat>
        </SimpleGrid>

        {recentSubmissions && recentSubmissions.length > 0 && (
          <Box bg="white" p={6} borderRadius="md" boxShadow="md">
            <Heading as="h2" size="md" mb={4}>
              Recent Submissions
            </Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Award Number</Th>
                  <Th>Risk Level</Th>
                  <Th>Impact Statement</Th>
                </Tr>
              </Thead>
              <Tbody>
                {recentSubmissions.map((submission) => (
                  <Tr key={submission.id}>
                    <Td>{formatDate(submission.createdAt)}</Td>
                    <Td>{submission.awardNumber}</Td>
                    <Td>
                      <Badge 
                        colorScheme={
                          submission.riskAssessment <= 2 ? "green" : 
                          submission.riskAssessment == 3 ? "yellow" : "red"
                        }
                      >
                        {submission.riskAssessment}
                      </Badge>
                    </Td>
                    <Td>
                      {submission.qualitativeResponse ? (
                        <Text noOfLines={1}>{submission.qualitativeResponse}</Text>
                      ) : (
                        <Text color="gray.500" fontSize="sm" fontStyle="italic">None provided</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        <Box bg="white" p={6} borderRadius="md" boxShadow="md">
          <Heading as="h2" size="md" mb={4}>
            About These Statistics
          </Heading>
          <Text>
            These statistics are generated from anonymized data submitted through our platform.
            The data is updated in real-time as new termination notices are reported.
          </Text>
          <Text mt={2}>
            Please note that this information represents only the cases reported through our
            platform and may not represent the full scope of NIH grant terminations.
          </Text>
          <Text mt={2}>
            <strong>{stats?.qualitative_response_count || 0}</strong> submitters have shared their experiences, and
            <strong> {stats?.contact_provided_count || 0}</strong> have provided contact information for follow-up.
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default Stats;